
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/utils/languageUtils';
import { Button } from '@/components/ui/button';

const BrokerSection: React.FC = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-fast');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative bg-gray-900 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-cover bg-center opacity-10"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900/95 to-indigo-900/30"></div>
      <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll will-change-transform">
              {t('broker.header')}
            </h2>
            
            <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll will-change-transform">
              {t('broker.subtitle')}
            </p>
            
            <div className="w-full max-w-xs mb-6 p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 animate-on-scroll will-change-transform">
              <img 
                src="/RM_logo-W.png" 
                alt="Radex Markets" 
                className="w-full h-auto"
              />
            </div>
            
            <p className="text-lg text-gray-300 mb-8 leading-relaxed animate-on-scroll will-change-transform">
              {t('broker.copy')}
            </p>
            
            <Button 
              size="lg" 
              className="inline-flex items-center px-6 py-3 bg-transparent border border-purple-500 text-purple-400 rounded-full hover:bg-purple-500/10 transition-all duration-300 animate-on-scroll will-change-transform"
              asChild
            >
              <Link to={getLocalizedPath('/radex', language)}>
                {t('broker.cta')}
              </Link>
            </Button>
          </div>
        </div>
        
        {/* Security badges */}
        <div className="flex flex-wrap justify-center gap-8 mt-16">
          <div className="flex flex-col items-center animate-on-scroll will-change-transform">
            <div className="flex flex-col items-center animate-on-scroll will-change-transform">
              <div className="w-16 h-16 flex items-center justify-center bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span className="text-sm text-gray-400">{t('broker.badge1')}</span>
            </div>
            
            <div className="flex flex-col items-center animate-on-scroll animate-delay-50 will-change-transform">
              <div className="w-16 h-16 flex items-center justify-center bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="text-sm text-gray-400">{t('broker.badge2')}</span>
            </div>
          </div>
          <div className="flex flex-col items-center animate-on-scroll will-change-transform">
            <div className="flex flex-col items-center animate-on-scroll animate-delay-100 will-change-transform">
              <div className="w-16 h-16 flex items-center justify-center bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="text-sm text-gray-400">{t('broker.badge3')}</span>
            </div>
            
            <div className="flex flex-col items-center animate-on-scroll animate-delay-150 will-change-transform">
              <div className="w-16 h-16 flex items-center justify-center bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <span className="text-sm text-gray-400">{t('broker.badge4')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrokerSection;
